// Shared translation of the backend's local-identity AuthTokenResponse into the redux `auth`
// slice shape. Every local sign-in page (password, OTP, signup) funnels through this so none of
// them can drift on field names. `loginUser` mirrors what pages/Callback/Callback.jsx builds for
// SSO, so role-based routing (utils/userRole.js) works the same for every sign-in method.
//
// `idToken` and `refreshToken` are deliberately null: they only exist for the SSO flow. Local
// sign-ins refresh via the HttpOnly cookie and end their session through POST /auth/logout.
export const authStateFromTokenResponse = (data = {}) => {
  const roles = Array.isArray(data.roles) ? data.roles : [];
  const username = data.username || [data.firstName, data.lastName].filter(Boolean).join(" ") || data.email || "";
  return {
    accessToken: data.accessToken ?? null,
    idToken: null,
    refreshToken: null,
    expiresAt: data.expiresIn ? Date.now() + data.expiresIn * 1000 : null,
    companyId: data.companyId ?? null,
    loginUser: {
      id: data.userId ?? null,
      userId: data.userId ?? null,
      companyId: data.companyId ?? null,
      roles,
      role: roles[0] || "",
      username,
      name: username,
      email: data.email || "",
      firstName: data.firstName ?? null,
      lastName: data.lastName ?? null,
    },
    sessionId: data.sessionId ?? null,
    authMethod: data.authMethod || "PASSWORD",
    refreshExpiresAt: data.refreshExpiresAt ?? null,
  };
};

const jwtExpiryMs = (token) => {
  try {
    const exp = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp;
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
};

/**
 * Maps the Garment backend's POST /api/auth/login (brand) or /api/auth/manufacturer/login
 * response - {jwtToken, userName, brand | manufacturer} - into the redux `auth` shape SSO uses.
 * The token itself carries no roles, so the role comes from which endpoint was called.
 */
export const authStateFromGarmentLogin = (data = {}, accountType) => {
  const role = accountType === "brand" ? "Brand" : "Manufacturer";
  const entity = (accountType === "brand" ? data.brand : data.manufacturer) || {};
  const userId = entity.primaryContactUserId ?? null;
  return {
    accessToken: data.jwtToken ?? null,
    idToken: null,
    refreshToken: null,
    expiresAt: data.jwtToken ? jwtExpiryMs(data.jwtToken) : null,
    companyId: entity.companyId ?? null,
    authMethod: "PASSWORD",
    loginUser: {
      ...entity,
      [accountType]: entity,
      id: userId,
      userId,
      companyId: entity.companyId ?? null,
      roles: [role],
      role,
      username: data.userName || "",
      name: entity.primaryContact || entity.name || data.userName || "",
      email: entity.email || "",
      mobileNumber: data.userName || "",
    },
  };
};

// Human-readable copy for backend local-auth error codes. The backend's `message` is used when a
// code is unknown to this table, so a new server-side code degrades to "whatever the server said"
// rather than to a blank alert.
const ERROR_COPY = {
  INVALID_CREDENTIALS: "Incorrect email or password.",
  ACCOUNT_LOCKED:
    "Too many failed attempts. This account is temporarily locked - try again in a few minutes.",
  ACCOUNT_BLOCKED: "This account has been deactivated. Ask your administrator for access.",
  ACCOUNT_NOT_FOUND: "No account found for that email. Ask your administrator to invite you.",
  PASSWORD_NOT_SET:
    "No password has been set for this account yet. Use the set-password link in your invitation email, or ask your administrator to resend it.",
  OTP_INVALID: "That code isn't right. Check it and try again.",
  OTP_EXPIRED: "That code has expired. Request a new one.",
  OTP_ATTEMPTS_EXCEEDED: "Too many incorrect codes. Request a new one.",
  OTP_RATE_LIMITED: "Too many codes requested. Wait a while before trying again.",
  TOKEN_INVALID: "This link is not valid.",
  TOKEN_EXPIRED: "This link has expired.",
  TOKEN_ALREADY_USED: "This link has already been used.",
  SESSION_REVOKED: "This session was signed out. Sign in again.",
  REFRESH_REUSE_DETECTED: "For your security this session was ended. Sign in again.",
  SIGNUP_DISABLED: "Self sign-up is switched off. Ask your administrator for an invitation.",
  EMAIL_ALREADY_REGISTERED: "An account already exists for that email. Sign in instead.",
  PASSWORD_MISMATCH: "The two passwords don't match.",
  PASSWORD_TOO_WEAK: "Use at least 8 characters.",
  EMAIL_FAILED:
    "We couldn't send that email just now. Try again in a moment, or ask your administrator.",
};

/**
 * Turns an axios rejection into a sentence for an inline MUI Alert.
 * The interceptors in utils/axios.js reject with the *response*, so the status is `err.status` and
 * the ErrorResponse envelope is `err.data` - not `err.response.*`.
 */
export const authErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  const code = err?.data?.error || err?.data?.code;
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  if (err?.data?.message) return err.data.message;
  // No response body at all - the backend is unreachable or CORS rejected the call.
  if (!err?.status) return "Can't reach the sign-in service. Check your connection and try again.";
  return fallback;
};

/** Client-side mirror of the backend's password policy. The server re-checks all of this - this
 *  only saves a round trip. */
export const validatePassword = (password = "", confirmPassword = "") => {
  if (password.length < 8) return "Use at least 8 characters.";
  if (password.length > 128) return "Use 128 characters or fewer.";
  if (password !== confirmPassword) return "The two passwords don't match.";
  return "";
};

export const isValidEmail = (value = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/** Local fallback for the request-OTP endpoints' `maskedEmail` when the server didn't send one. */
export const maskEmail = (value = "") => {
  const [name, domain] = String(value).split("@");
  if (!name || !domain) return value;
  const visible = name.slice(0, Math.min(2, name.length));
  return `${visible}${"*".repeat(Math.max(1, name.length - visible.length))}@${domain}`;
};
